import { Link } from "react-router-dom";
import { ArrowRight, Star, Clock, MapPin, Sparkles } from "lucide-react";
import { BRAND, MEDIA, MENU, BRANCHES } from "../lib/bakeryData";

const Home = () => {
    const featured = [
        {
            ...MENU["Pastries"][4],
            category: "Pastry",
            img: "https://customer-assets.emergentagent.com/job_local-bakery-hub-3/artifacts/yspr6dyk_Dutch%20pastry.png",
            desc: "Dense chocolate sponge, dark ganache, finished with a velvet swirl.",
        },
        {
            ...MENU["Handcrafted Breads"][4],
            category: "Bread",
            img: "https://customer-assets.emergentagent.com/job_local-bakery-hub-3/artifacts/0ccu5htu_Multigrain%20bread.png",
            desc: "Oats, flax, sunflower & sesame — wholesome, hearty, sliced fresh.",
        },
        {
            ...MENU["Cakes"][9],
            category: "Cake",
            img: "https://customer-assets.emergentagent.com/job_local-bakery-hub-3/artifacts/p2yqqxgg_BELGIAN%20CAKE%20NEW%20DESIGN.PNG",
            desc: "Hand-piped Belgian dark ganache, gold pearls, signature seal.",
        },
    ];

    return (
        <main data-testid="home-page" className="bg-[#FAF8F5]">
            {/* HERO */}
            <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-25 pointer-events-none"
                    style={{
                        backgroundImage: `url(${MEDIA.heroTexture})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5]/40 via-[#FAF8F5]/85 to-[#FAF8F5] pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="fade-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#E8D8C8] bg-white/60 text-xs uppercase tracking-[0.18em] text-[#6D4C41]">
                            <Sparkles className="w-3.5 h-3.5 text-[#C5832B]" />
                            Est. {BRAND.foundedOn}
                        </div>

                        <h1
                            data-testid="home-hero-title"
                            className="fade-up delay-1 font-serif text-5xl sm:text-6xl lg:text-7xl text-[#3E2723] leading-[1.05] mt-6"
                        >
                            Warm bread. <br />
                            <span className="italic text-[#C5832B]">Sweeter</span> mornings.
                        </h1>

                        <p className="fade-up delay-2 mt-6 max-w-xl text-base md:text-lg text-[#6D4C41] leading-relaxed">
                            Three little bakeries in Chhattisgarh, one obsession with
                            butter, flour, and the people we feed. Hand-laminated croissants,
                            slow-fermented loaves and celebration cakes — baked fresh every
                            single morning.
                        </p>

                        <div className="fade-up delay-3 mt-9 flex flex-wrap items-center gap-4">
                            <Link
                                to="/menu"
                                data-testid="home-explore-menu-btn"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3E2723] text-[#FAF8F5] text-sm hover:bg-[#C5832B] transition-all hover:scale-[1.02]"
                            >
                                Explore the menu
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/branches"
                                data-testid="home-order-online-btn"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#3E2723]/20 text-[#3E2723] text-sm hover:bg-[#3E2723] hover:text-[#FAF8F5] transition-all"
                            >
                                Order on Swiggy & Zomato
                            </Link>
                        </div>

                        <div className="fade-up delay-4 mt-12 flex items-center gap-6 flex-wrap text-sm text-[#6D4C41]">
                            <div className="flex items-center gap-2">
                                <Star className="w-4 h-4 text-[#C5832B] fill-[#C5832B]" />
                                <span>Loved across 3 towns</span>
                            </div>
                            <span className="w-1 h-1 rounded-full bg-[#6D4C41]/30" />
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-[#C5832B]" />
                                <span>Baked since 4 AM</span>
                            </div>
                            <span className="w-1 h-1 rounded-full bg-[#6D4C41]/30" />
                            <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#C5832B]" />
                                <span>3 branches</span>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative fade-up delay-2">
                        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-[#E8D8C8] shadow-[0_30px_80px_-30px_rgba(62,39,35,0.35)]">
                            <img
                                src={MEDIA.pastries}
                                alt="Freshly baked pastries"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-[#FAF8F5]/85 rounded-2xl p-4 border border-white/40">
                                <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                    Today's bake
                                </p>
                                <p className="font-serif text-xl text-[#3E2723] mt-1">
                                    Almond Croissants, still warm
                                </p>
                            </div>
                        </div>
                        <div className="hidden md:block absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#C5832B]/15 blur-3xl" />
                        <div className="hidden md:block absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[#3E2723]/10 blur-3xl" />
                    </div>
                </div>
            </section>

            {/* MARQUEE STRIP */}
            <section className="border-y border-[#E8D8C8] bg-[#F3EBE1]/60 overflow-hidden py-5">
                <div className="marquee-track flex items-center gap-12 whitespace-nowrap">
                    {Array.from({ length: 2 }).map((_, k) => (
                        <div key={k} className="flex items-center gap-12">
                            {[
                                "Sourdough",
                                "Croissants",
                                "Celebration Cakes",
                                "Cookies",
                                "Cinnamon Rolls",
                                "Eclairs",
                                "Focaccia",
                                "Brownies",
                            ].map((w, i) => (
                                <span
                                    key={`${k}-${i}`}
                                    className="font-serif italic text-2xl text-[#3E2723]/65 flex items-center gap-12"
                                >
                                    {w}
                                    <span className="text-[#C5832B]">✦</span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div>
                        <p className="font-handwriting text-3xl text-[#C5832B] leading-none">
                            From our counter
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3">
                            What everyone keeps coming back for
                        </h2>
                    </div>
                    <Link
                        to="/menu"
                        data-testid="home-view-full-menu"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-wide text-[#3E2723] hover:text-[#C5832B] transition-colors"
                    >
                        View full menu <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {featured.map((item, i) => (
                        <article
                            key={item.name}
                            data-testid={`home-featured-${i}`}
                            className="group rounded-3xl overflow-hidden bg-white border border-[#E8D8C8] hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(62,39,35,0.35)] transition-all duration-500"
                        >
                            <div className="aspect-[5/4] overflow-hidden">
                                <img
                                    src={item.img}
                                    alt={item.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="p-7">
                                <p className="text-xs uppercase tracking-[0.18em] text-[#C5832B]">
                                    {item.category}
                                </p>
                                <h3 className="font-serif text-2xl text-[#3E2723] mt-2">
                                    {item.name}
                                </h3>
                                <p className="text-sm text-[#6D4C41] mt-2 leading-relaxed">
                                    {item.desc}
                                </p>
                                <p className="font-handwriting text-2xl text-[#C5832B] mt-4">
                                    {item.price}
                                </p>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* STORY TEASER */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
                <div className="grid lg:grid-cols-12 gap-12 items-center bg-[#F3EBE1] rounded-[2rem] p-8 md:p-14 border border-[#E8D8C8]">
                    <div className="lg:col-span-5">
                        <div className="aspect-square rounded-2xl overflow-hidden border border-[#E8D8C8]">
                            <img
                                src={MEDIA.foundersPortrait}
                                alt="Founders Aaditya & Aditya"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="font-handwriting text-3xl text-[#C5832B]">Our story</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3 leading-tight">
                            Two childhood friends. <br />
                            One little oven in <span className="italic">Ambikapur</span>.
                        </h2>
                        <p className="text-base text-[#6D4C41] mt-6 leading-relaxed max-w-xl">
                            On <strong>24th May 2023</strong>, {BRAND.founders[0]} and{" "}
                            {BRAND.founders[1]} flipped the open sign on their very first
                            bakery in Ambikapur. Two more homes followed — Surajpur and
                            Bishrampur — and the smell of warm butter has been chasing them
                            around Chhattisgarh ever since.
                        </p>
                        <Link
                            to="/about"
                            data-testid="home-read-story-btn"
                            className="inline-flex items-center gap-2 mt-8 text-sm uppercase tracking-wide text-[#3E2723] hover:text-[#C5832B] transition-colors"
                        >
                            Read the full story <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* BRANCHES PREVIEW */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <p className="font-handwriting text-3xl text-[#C5832B]">
                        Drop by, we'll save you a seat
                    </p>
                    <h2 className="font-serif text-4xl md:text-5xl text-[#3E2723] mt-3">
                        Three branches, one warm welcome
                    </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-7">
                    {BRANCHES.map((b) => (
                        <Link
                            key={b.id}
                            to="/branches"
                            data-testid={`home-branch-card-${b.id}`}
                            className="group relative rounded-2xl overflow-hidden border border-[#E8D8C8] bg-white hover:-translate-y-1 transition-all duration-500"
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <img
                                    src={b.image}
                                    alt={b.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="p-6">
                                <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                    {b.tagline}
                                </p>
                                <h3 className="font-serif text-2xl text-[#3E2723] mt-1">
                                    {b.name}
                                </h3>
                                <p className="text-sm text-[#6D4C41] mt-2">{b.address}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </main>
    );
};

export default Home;
